import { defineCopy } from '@floorfile/i18n'

/**
 * Record discussions — comments (localization plan Phase 5, chunk 5e-1).
 *
 * `<CommentsPanel>` is the one component that renders on every host detail page
 * in the product, so its words are read more often than any single feature's.
 * Register is the same colleague-to-colleague voice as chat.
 *
 * ## The inbox groups are NOT a contract enum
 *
 * `InboxItem.entityType` is `z.string()` in `packages/contracts` — the server
 * derives it from whichever module owns the host record — so there is no enum
 * to be the completeness guard here and no other slice that owns the words.
 * `group` therefore holds them, and both places that render an entity type keep
 * a fallback to the raw code: a code is ugly, but it is honest, and inventing a
 * label for a record kind this build has never heard of would be worse.
 *
 * ## Deliberately NOT translated
 *
 * A thread's own `title` and a comment's `body` are what someone typed. The
 * quick-reaction emoji are not words. `entityLabel` is a record reference
 * (`CAR-2026-0003 · Belt tears`) composed by the owning module's resolver.
 */
export const commentsCopy = defineCopy('comments', {
  offlineAction: { add: 'Add comment' },
  panel: {
    /** French inflects the noun; English does not. The count is absent from the
     * heading until a default thread exists, hence two entries. */
    title: 'Comments',
    /** FF-AUDIT-097: the panel stays usable in a dead zone — a comment typed here queues with the record. */
    offlineNote: 'Earlier comments are not stored on this device. A comment you add now is queued and sent when you are back in range.',
    titleWithCount: { one: 'Comments ({count})', other: 'Comments ({count})' },
    watch: 'Watch this thread',
    stopWatching: 'Stop watching',
    resolveTitle: 'Mark discussion resolved',
    reopenTitle: 'Re-open the discussion',
    resolve: 'Resolve',
    reopen: 'Re-open',
    /** The unnamed default thread's tab. */
    general: 'General',
    threadTitleAria: 'Thread title',
    threadTitlePlaceholder: 'Thread title…',
    addThread: '+ thread',
    addNamedThread: '+ named thread',
    /** Byte-locked: `chat-comments.spec.ts` asserts this exact string. */
    resolvedNotice: 'This discussion is marked resolved.',
  },

  comment: {
    /** FF-AUDIT-088: a comment queued offline, shown in the thread until it posts. */
    pendingSync: 'Queued on this device — posts when back online',
    deleted: 'comment deleted',
    someone: 'Someone',
    edited: '(edited)',
    editAria: 'Edit comment',
    save: 'Save',
    reply: 'Reply',
    edit: 'Edit',
    delete: 'Delete',
    /** Two messages rather than one plus a conditional suffix: a comma-appended
     * clause is concatenation, and French would not necessarily put the state
     * last. */
    reactionAria: '{emoji} {count}',
    reactionAriaReacted: '{emoji} {count}, reacted',
    reactAria: 'React with {emoji}',
    empty: 'No comments yet — start the discussion.',
    replyingTo: 'Replying to <0>{name}</0>',
    cancelReply: 'Cancel reply',
    /** Byte-locked: `chat-comments.spec.ts` fills by this placeholder. */
    placeholder: 'Comment — @ mentions someone',
    /** FF-QA-056 */
    post: 'Post',
    composerHint: 'Enter posts · Shift+Enter for a new line',
    postFailed: 'Could not post.',
  },

  inbox: {
    title: 'Inbox',
    description: 'Unread record discussions that involve you — mentions and threads you watch.',
    markAllRead: 'Mark all read',
    /** Byte-locked: `chat-comments.spec.ts` asserts this exact string. */
    emptyTitle: 'All caught up',
    emptyDescription: 'When someone mentions you or comments on a thread you watch, it lands here.',
    /** No plural selector: nothing in this heading inflects for the count —
     * `{label}` is already a plural noun and the number sits in brackets. */
    groupHeading: '{label} ({count})',
    /** The record kinds a discussion can hang off. Not an enum — see the header. */
    group: {
      check: 'Checks',
      car: 'Corrective actions',
      incident: 'Incidents',
      work_order: 'Work orders',
      audit: 'Audits',
      document: 'Documents',
    },
  },
})

export type CommentsCopy = typeof commentsCopy.en
